import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getMovieDetails } from '../services/movieService';
import { TMDB_IMAGE_BASE_URL } from '../config';
import ItemDetail from './shared/ItemDetail';
import './MovieDetail.css';

function MovieDetail() {
  const { id } = useParams();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const fetchMovieDetails = async () => {
      try {
        setLoading(true);
        setRetrying(false);
        const data = await getMovieDetails(id);
        setMovie(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching movie details:', err);
        setError('Failed to fetch movie details. The system is automatically retrying. Please wait or try again later.');
        setRetrying(true);
        
        // Try again after a delay if it's a network error
        if (err.message.includes('network') || err.message.includes('fetch')) {
          setTimeout(() => {
            setRetrying(false);
            fetchMovieDetails();
          }, 3000);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMovieDetails();
  }, [id]);

  // Create image component for the movie
  const movieImageComponent = movie?.poster_path ? (
    <img
      src={`${TMDB_IMAGE_BASE_URL}${movie.poster_path}`}
      alt={movie.title}
      className="item-image movie-poster"
    />
  ) : null;

  // Create metadata object for the movie in the exact specified order:
  // Release Date, Rating, Genres, Director
  const movieMetadata = movie ? {
    'Release Date': movie.release_date || 'Unknown',
    'Rating': movie.vote_average ? `${movie.vote_average.toFixed(1)}/10` : 'Not rated',
    'Genres': <span className="genre-value">{movie.genres?.map(g => g.name).join(', ') || 'Unknown'}</span>,
    'Director': movie.director || 'Unknown'
  } : {};

  // Create extra content for the movie - now with Overview and Trailer using the same styling pattern
  const movieExtraContent = movie ? (
    <>
      {movie.overview && (
        <div className="item-description movie-overview">
          <h3>Overview</h3>
          <p>{movie.overview}</p>
        </div>
      )}
      {movie.trailerLink && (
        <div className="item-description movie-trailer">
          <h3>Trailer</h3>
          <p>
            <a href={movie.trailerLink} target="_blank" rel="noopener noreferrer" className="trailer-link">
              Watch Trailer
            </a>
          </p>
        </div>
      )}
    </>
  ) : null;

  return (
    <ItemDetail
      type="movie"
      item={movie}
      loading={loading}
      error={error}
      retrying={retrying}
      imageComponent={movieImageComponent}
      metadata={movieMetadata}
      extraContent={movieExtraContent}
    />
  );
}

export default MovieDetail; 